import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Grid,
  CircularProgress
} from '@mui/material';
import { useMemo } from 'react';
import { useLanguage } from '../../../../utils/i18n';
import dayjs from 'dayjs';
import { HtmlTableContainer, HtmlTable, StyledTableCell, StyledTableRow, getActionStyles } from '../historical/TireHistoricalPage';
import { formatPositionInfo, formatNumber, formatCurrency } from './tirePerformanceUtils';
import { useTireHistoricalList } from '../../../../stores/tireHistoricalStore';

const StatItem = ({ label, value, caption }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 0.25 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e293b' }}>
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem', lineHeight: 1.2 }}>
        {caption}
      </Typography>
    )}
  </Box>
);

const displayValue = (val) => (val === null || val === undefined || val === '' ? '-' : val);

// Columns before "Cost Repair" (No .. Repair Type) and after it (Tire Damage, Mechanic Name)
const COLS_BEFORE_COST = 13;
const COLS_AFTER_COST = 2;
const TOTAL_COLS = COLS_BEFORE_COST + 1 + COLS_AFTER_COST;

const footerLabelStyle = {
  padding: '12px 16px',
  textAlign: 'right',
  fontSize: '0.875rem',
  color: '#475569',
  borderBottom: '1px solid #e2e8f0'
};
const footerValueStyle = {
  padding: '12px 16px',
  fontSize: '0.875rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #e2e8f0'
};
const footerEmptyStyle = { borderBottom: '1px solid #e2e8f0' };

export default function TirePerformanceHistoryModal({ open, onClose, data }) {
  const { t } = useLanguage();
  const serialNumbers = data?.serial_number ? [{ value: data.serial_number }] : [];
  const companies = data?.company_id ? [{ value: data.company_id }] : [];

  const dateRange = useMemo(() => {
    if (data?.start_time && data?.end_time) {
      return {
        startDate: dayjs(data.start_time).format('YYYY-MM-DD'),
        endDate: dayjs(data.end_time).format('YYYY-MM-DD')
      };
    }
    return null;
  }, [data?.start_time, data?.end_time]);

  const { data: fetchedHistory, isLoading } = useTireHistoricalList(0, 9999, null, null, companies, dateRange, serialNumbers);

  if (!data) return null;

  const history = Array.isArray(fetchedHistory) ? fetchedHistory : [];
  const sortedHistory = [...history].sort((a, b) => {
    const timeA = a.transaction_date ? new Date(a.transaction_date).getTime() : 0;
    const timeB = b.transaction_date ? new Date(b.transaction_date).getTime() : 0;
    if (timeA !== timeB) return timeA - timeB;
    // Fallback to created_at or id
    const createdA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const createdB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return createdA - createdB;
  });
  const specString = [data.tire_spec_name, data.tire_brand_name, data.tire_size_code_internal || data.tire_size_code]
    .filter(Boolean)
    .join(' - ');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', p: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem' }}>{t('tireHistorical.tireHistory', 'Tire History')}</Typography>
          <Chip label={data.serial_number || '-'} size="small" color="primary" sx={{ fontWeight: 700 }} />
          <Chip
            label={`${sortedHistory.length} ${t('transaction', 'transaction')}${sortedHistory.length === 1 ? '' : 's'}`}
            size="small"
            variant="outlined"
          />
        </Stack>
        {specString && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {specString}
          </Typography>
        )}

        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            py: 1.5,
            px: 2,
            mt: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item size={{ xs: 6, md: 2 }}>
              <StatItem
                label={t('tirePerformance.initKmHm', 'Init KM / HM')}
                value={`${formatNumber(data.init_km_unit)} / ${formatNumber(data.init_hm_unit)}`}
              />
            </Grid>
            <Grid item size={{ xs: 6, md: 2 }}>
              <StatItem label={t('tirePerformance.totalDistance', 'Total Distance')} value={`${formatNumber(data.total_km, 2)} km`} />
            </Grid>
            <Grid item size={{ xs: 6, md: 2 }}>
              <StatItem label={t('tirePerformance.totalHour', 'Total Hour')} value={`${formatNumber(data.total_hm, 2)} h`} />
            </Grid>
            <Grid item size={{ xs: 6, md: 3 }}>
              <StatItem
                label={t('tireRepairScrap.totalCost', 'Total Cost')}
                value={formatCurrency(data.total_cost)}
                caption={`${t('tireReceiving.unit', 'Unit')}: ${formatCurrency(data.unit_price)} + ${t('tireRepairScrap.repair', 'Repair')}: ${formatCurrency(data.total_repair_cost)}`}
              />
            </Grid>
            <Grid item size={{ xs: 6, md: 1.5 }}>
              <StatItem label="CPK" value={formatCurrency(data.cpk, 2)} />
            </Grid>
            <Grid item size={{ xs: 6, md: 1.5 }}>
              <StatItem label="CPH" value={formatCurrency(data.cph, 2)} />
            </Grid>
          </Grid>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 0, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ pt: 2, flex: 1, display: 'flex', minHeight: 0 }}>
          <HtmlTableContainer style={{ flex: 1, overflow: 'auto' }}>
            <HtmlTable style={{ minWidth: 1550 }}>
              <thead>
                <tr>
                  <StyledTableCell
                    style={{ position: 'sticky', top: 0, left: 0, width: 50, minWidth: 50, zIndex: 4, boxSizing: 'border-box' }}
                  >
                    {t('tireRepairScrap.no', 'No')}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ position: 'sticky', top: 0, left: 50, width: 150, minWidth: 150, zIndex: 4, boxSizing: 'border-box' }}
                  >
                    {t('tireRepairScrap.serialNumberExport', 'Serial Number')}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ position: 'sticky', top: 0, left: 200, width: 140, minWidth: 140, zIndex: 4, boxSizing: 'border-box' }}
                  >
                    {t('tireRepairScrap.transactionDate', 'Transaction Date')}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      position: 'sticky',
                      top: 0,
                      left: 340,
                      width: 150,
                      minWidth: 150,
                      zIndex: 4,
                      boxSizing: 'border-box',
                      borderRight: '2px solid #cbd5e1'
                    }}
                  >
                    {t('tireCardHistory.actionType', 'Transaction Type')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireHistorical.licensePlate', 'License Plate')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.kmUnit', 'KM Unit')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.hmUnit', 'HM Unit')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tirePerformance.totalKm', 'Total KM')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tirePerformance.totalHm', 'Total HM')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireRepairScrap.currentPosition', 'Current Position')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.repairType', 'Repair Type')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.costRepair', 'Cost Repair')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.tireDamage', 'Tire Damage')}
                  </StyledTableCell>
                  <StyledTableCell style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                    {t('tireCardHistory.mechanicName', 'Mechanic Name')}
                  </StyledTableCell>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={TOTAL_COLS} align="center" style={{ padding: '48px 0' }}>
                      <CircularProgress size={32} thickness={5} />
                    </td>
                  </tr>
                ) : sortedHistory.length === 0 ? (
                  <tr>
                    <td colSpan={TOTAL_COLS} align="center" style={{ padding: '48px 0' }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('tireHistorical.noHistoryFound', 'No history found.')}
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  sortedHistory.map((row, index) => {
                    const actionStyles = getActionStyles(row.transaction_type);
                    return (
                      <StyledTableRow key={row.id || index}>
                        <td
                          style={{
                            position: 'sticky',
                            left: 0,
                            width: 50,
                            minWidth: 50,
                            zIndex: 1,
                            backgroundColor: 'inherit',
                            boxSizing: 'border-box'
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            position: 'sticky',
                            left: 50,
                            width: 150,
                            minWidth: 150,
                            zIndex: 1,
                            backgroundColor: 'inherit',
                            boxSizing: 'border-box',
                            fontWeight: 600
                          }}
                        >
                          {displayValue(row.serial_number)}
                        </td>
                        <td
                          style={{
                            position: 'sticky',
                            left: 200,
                            width: 140,
                            minWidth: 140,
                            zIndex: 1,
                            backgroundColor: 'inherit',
                            boxSizing: 'border-box'
                          }}
                        >
                          {row.transaction_date ? dayjs(row.transaction_date).format('DD MMM YYYY') : '-'}
                        </td>
                        <td
                          style={{
                            position: 'sticky',
                            left: 340,
                            width: 150,
                            minWidth: 150,
                            zIndex: 1,
                            backgroundColor: 'inherit',
                            boxSizing: 'border-box',
                            borderRight: '2px solid #cbd5e1'
                          }}
                        >
                          <Chip
                            label={row.transaction_type || '-'}
                            size="small"
                            sx={{
                              bgcolor: actionStyles.bg,
                              color: actionStyles.color,
                              fontWeight: 700,
                              borderRadius: 1.5,
                              letterSpacing: 0.5
                            }}
                          />
                        </td>
                        <td>{displayValue(row.license_plate)}</td>
                        <td>{formatNumber(row.km_unit)}</td>
                        <td>{formatNumber(row.hm_unit)}</td>
                        <td>{formatNumber(row.total_km)}</td>
                        <td>{formatNumber(row.total_hm)}</td>
                        <td>{formatPositionInfo(row.current_position, row.serial_number)}</td>
                        <td>{displayValue(row.air_pressure)}</td>
                        <td>{displayValue(row.tread_depth)}</td>
                        <td>{displayValue(row.repair_type)}</td>
                        <td>{formatCurrency(row.cost_repair)}</td>
                        <td>{displayValue(row.tire_damage_name)}</td>
                        <td>{displayValue(row.mechanic_name)}</td>
                      </StyledTableRow>
                    );
                  })
                )}
              </tbody>
              {sortedHistory.length > 0 && (
                <tfoot style={{ backgroundColor: '#f8fafc' }}>
                  <tr>
                    <td colSpan={COLS_BEFORE_COST} style={footerLabelStyle}>
                      {t('tireReceiving.unitPrice', 'Unit Price')}
                    </td>
                    <td style={footerValueStyle}>{formatCurrency(data.unit_price)}</td>
                    <td colSpan={COLS_AFTER_COST} style={footerEmptyStyle} />
                  </tr>
                  <tr>
                    <td colSpan={COLS_BEFORE_COST} style={footerLabelStyle}>
                      {t('tirePerformance.totalRepairCost', 'Total Cost Repair')}
                    </td>
                    <td style={footerValueStyle}>{formatCurrency(data.total_repair_cost)}</td>
                    <td colSpan={COLS_AFTER_COST} style={footerEmptyStyle} />
                  </tr>
                  <tr>
                    <td colSpan={COLS_BEFORE_COST} style={{ ...footerLabelStyle, fontWeight: 700, color: '#0f172a' }}>
                      {t('tirePerformance.totalCostDetail', 'Total Cost (Unit Price + Total Cost Repair)')}
                    </td>
                    <td style={{ ...footerValueStyle, fontWeight: 800, color: '#5166AE' }}>{formatCurrency(data.total_cost)}</td>
                    <td colSpan={COLS_AFTER_COST} style={footerEmptyStyle} />
                  </tr>
                </tfoot>
              )}
            </HtmlTable>
          </HtmlTableContainer>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} variant="contained" disableElevation sx={{ textTransform: 'none' }}>
          {t('tireRepairScrap.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
